import { useQuery, useQueryClient } from '@tanstack/react-query';
import { update } from 'ramda';
import { useMemo, useCallback } from 'react';

import { readMenuAll } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Item, Menu, MenuContainer, Option } from 'apis/restaurant-api/merchant-admin/menu2/types';

import { useMenuModuleContext } from './context';

type UpdateItemAction = {
  actionType: 'updateItem';
  payload: Item;
};

type UpdateOptionAction = {
  actionType: 'updateOption';
  payload: Option;
};

type UpdateMenuAction = {
  actionType: 'updateMenu';
  payload: Menu;
};

type UpdateMenuContainerAction = {
  actionType: 'updateMenuContainer';
  payload: MenuContainer;
};

type AddItemAction = {
  actionType: 'addItem';
  payload: Item;
};

type AddOptionAction = {
  actionType: 'addOption';
  payload: Option;
};

type UpdateMenuDataAction =
  | AddItemAction
  | AddOptionAction
  | UpdateItemAction
  | UpdateOptionAction
  | UpdateMenuAction
  | UpdateMenuContainerAction;

const useMenuData = (enabled: boolean = true) => {
  const { venue } = useMenuModuleContext();
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => {
    return ['read-menu-all', venue.menu];
  }, [venue.menu]);

  const {
    data: menuData,
    isFetching: loadingMenuData,
    error: menuDataError,
    refetch: refetchMenuData,
  } = useQuery(
    queryKey,
    () => {
      if (venue.menu) {
        return readMenuAll(venue.menu);
      } else {
        return Promise.resolve(null);
      }
    },
    {
      enabled,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  const updateMenuContainer = useCallback(
    (updatedMenuContainer: MenuContainer) => {
      queryClient.setQueryData(queryKey, updatedMenuContainer);
    },
    [queryClient, queryKey],
  );

  const addItem = useCallback(
    (newItem: Item) => {
      queryClient.setQueryData(queryKey, (oldData: MenuContainer | undefined) => {
        if (!oldData) {
          throw new Error('No menu data fetched, this should not happen!');
        }
        const updatedItems = [...oldData.items, newItem];
        return { ...oldData, items: updatedItems };
      });
    },
    [queryClient, queryKey],
  );

  const updateItem = useCallback(
    (updatedItem: Item) => {
      queryClient.setQueryData(queryKey, (oldData: MenuContainer | undefined) => {
        if (!oldData) {
          throw new Error('No menu data fetched, this should not happen!');
        }
        const itemIndex = oldData.items.findIndex(item => item.id === updatedItem.id);
        const updatedItems = update(itemIndex, updatedItem, oldData.items);
        return { ...oldData, items: updatedItems };
      });
    },
    [queryClient, queryKey],
  );

  const addOption = useCallback(
    (newOption: Option) => {
      queryClient.setQueryData(queryKey, (oldData: MenuContainer | undefined) => {
        if (!oldData) {
          throw new Error('No menu data fetched, this should not happen!');
        }
        const updatedOptions = [...oldData.options, newOption];
        return {
          ...oldData,
          options: updatedOptions,
        };
      });
    },
    [queryClient, queryKey],
  );

  const updateOption = useCallback(
    (updatedOption: Option) => {
      queryClient.setQueryData(queryKey, (oldData: MenuContainer | undefined) => {
        if (!oldData) {
          throw new Error('No menu data fetched, this should not happen!');
        }

        const optionIndex = oldData.options.findIndex(o => o.id === updatedOption.id);
        const updatedOptions = update(optionIndex, updatedOption, oldData.options);
        return {
          ...oldData,
          options: updatedOptions,
        };
      });
    },
    [queryClient, queryKey],
  );

  const updateMenu = useCallback(
    (updatedMenu: Menu) => {
      queryClient.setQueryData(queryKey, (oldData: MenuContainer | undefined) => ({ ...oldData, menu: updatedMenu }));
    },
    [queryClient, queryKey],
  );

  const updateMenuData = useCallback(
    (action: UpdateMenuDataAction) => {
      switch (action.actionType) {
        case 'updateItem':
          updateItem(action.payload);
          return;
        case 'updateOption':
          updateOption(action.payload);
          return;
        case 'updateMenu':
          updateMenu(action.payload);
          return;
        case 'updateMenuContainer':
          updateMenuContainer(action.payload);
          return;
        case 'addItem':
          addItem(action.payload);
          return;
        case 'addOption':
          addOption(action.payload);
          return;
        default:
          return;
      }
    },
    [addItem, addOption, updateItem, updateMenu, updateMenuContainer, updateOption],
  );

  return {
    items: menuData?.items ?? [],
    categories: menuData?.menu.categories ?? [],
    options: menuData?.options ?? [],
    menu: menuData?.menu,
    loadingMenuData,
    menuDataError,
    refetchMenuData,
    updateMenuData,
  };
};

export default useMenuData;
